import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import type {
  ServiceTypeCreateRequest,
  ServiceTypeDto,
  ServiceTypeUpdateRequest,
  VisitClosingRequest,
  VisitCreateRequest,
  VisitDetailResponse,
  VisitDto,
  VisitOverlap,
  VisitUpdateRequest,
} from '@kominiarz/shared';
import { api } from './api';
import type { DateRange } from './calendarDates';
import { queryClient } from './queryClient';

export const VISITS_KEY = ['visits'] as const;
export const SERVICE_TYPES_KEY = ['service-types'] as const;

/** API filter: 'all', 'mine' or 'user:<id>'. */
export type VisitFilter = string;

export function useVisits(range: DateRange, filter: VisitFilter) {
  const from = range.start.toISO()!;
  const to = range.end.toISO()!;
  return useQuery({
    queryKey: [...VISITS_KEY, 'list', from, to, filter],
    queryFn: async ({ signal }) =>
      (
        await api<{ visits: VisitDto[] }>(
          `/visits?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&filter=${encodeURIComponent(filter)}`,
          { signal },
        )
      ).visits,
    // Keep the previous period on screen while the next one loads (no flashing).
    placeholderData: keepPreviousData,
  });
}

export function useVisit(id: number | null) {
  return useQuery({
    queryKey: [...VISITS_KEY, 'detail', id],
    queryFn: ({ signal }) => api<VisitDetailResponse>(`/visits/${id}`, { signal }),
    enabled: id !== null,
  });
}

export function useOverlaps(
  params: {
    assigneeIds: number[];
    startAt: string;
    durationMinutes: number;
    arrivalWindowMinutes: number | null;
    excludeId?: number;
  } | null,
) {
  return useQuery({
    queryKey: [...VISITS_KEY, 'overlaps', params],
    queryFn: async ({ signal }) => {
      const p = params!;
      const qs = new URLSearchParams({
        assigneeIds: p.assigneeIds.join(','),
        startAt: p.startAt,
        durationMinutes: String(p.durationMinutes),
        ...(p.arrivalWindowMinutes ? { arrivalWindowMinutes: String(p.arrivalWindowMinutes) } : {}),
        ...(p.excludeId ? { excludeId: String(p.excludeId) } : {}),
      });
      return (await api<{ overlaps: VisitOverlap[] }>(`/visits/overlaps?${qs}`, { signal })).overlaps;
    },
    enabled: params !== null,
    staleTime: 10_000,
  });
}

const invalidateVisits = () => queryClient.invalidateQueries({ queryKey: VISITS_KEY });

export function useCreateVisit() {
  return useMutation({
    mutationFn: (body: VisitCreateRequest) => api<{ visit: VisitDto }>('/visits', { method: 'POST', body }),
    onSuccess: invalidateVisits,
  });
}

export function useUpdateVisit() {
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: VisitUpdateRequest }) =>
      api<{ visit: VisitDto }>(`/visits/${id}`, { method: 'PATCH', body }),
    onSuccess: invalidateVisits,
  });
}

export function useCloseVisit() {
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: VisitClosingRequest }) =>
      api<{ visit: VisitDto }>(`/visits/${id}/closing`, { method: 'PATCH', body }),
    onSuccess: invalidateVisits,
  });
}

export function useDeleteVisit() {
  return useMutation({
    mutationFn: (id: number) => api<{ ok: true }>(`/visits/${id}`, { method: 'DELETE' }),
    onSuccess: invalidateVisits,
  });
}

export function useServiceTypes(includeInactive = false) {
  return useQuery({
    queryKey: [...SERVICE_TYPES_KEY, includeInactive],
    queryFn: async () =>
      (await api<{ serviceTypes: ServiceTypeDto[] }>(`/service-types${includeInactive ? '?all=1' : ''}`)).serviceTypes,
    staleTime: 5 * 60_000,
  });
}

const invalidateServiceTypes = () => queryClient.invalidateQueries({ queryKey: SERVICE_TYPES_KEY });

export function useCreateServiceType() {
  return useMutation({
    mutationFn: (body: ServiceTypeCreateRequest) =>
      api<{ serviceType: ServiceTypeDto }>('/service-types', { method: 'POST', body }),
    onSuccess: invalidateServiceTypes,
  });
}

/** Saves the new display order (all ids, first to last). */
export function useReorderServiceTypes() {
  return useMutation({
    mutationFn: (ids: number[]) => api<{ ok: true }>('/service-types/order', { method: 'PUT', body: { ids } }),
    onSuccess: invalidateServiceTypes,
  });
}

export function useUpdateServiceType() {
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: ServiceTypeUpdateRequest }) =>
      api<{ serviceType: ServiceTypeDto }>(`/service-types/${id}`, { method: 'PATCH', body }),
    onSuccess: invalidateServiceTypes,
  });
}
